'use client';

import ThreeButtonsTopBar from '@/app/ui/common/three-buttons-top-bar';
import BackBlack814 from '@svg/common/back_black_8_14.svg';
import HomeBlack2424 from '@svg/common/home_black_24_24.svg';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TravelCardCGType } from '@/types/graphql/root';
import { getListTravelCards, getTravelCardCategory } from '@/utils/actions';
import Link from 'next/link';
import TravelCardSkeleton from '../ui/main/travel-card-skeleton';
import TravelCard from '../ui/main/travel-card';
import Hangul from 'hangul-js';
import SearchInputBarBasic from '../ui/common/search-input-bar-basic';
import SelectBoxSmall from '../ui/common/select-box-small';

const TopBarLeftButton = () => {
  const router = useRouter();

  return (
    <button
      className='w-24pxr h-24pxr flex items-center justify-center'
      onClick={() => {
        router.back();
      }}
    >
      <BackBlack814 />
    </button>
  );
};

const TopBarCenterTitle = () => {
  return <h3 className='text-new-gray-900 body-16-md'>여행지 둘러보기</h3>;
};

const TopBarRightButton = () => {
  return (
    <Link href='/' scroll={true} prefetch={false} className='w-24pxr h-24pxr flex items-center justify-center'>
      <HomeBlack2424 />
    </Link>
  );
};

const TravelCardPageSkeleton = () => {
  return (
    <>
      <div className='w-full h-38pxr border border-new-gray-300 rounded-8pxr py-9pxr pl-14pxr pr-8pxr flex items-center justify-between'>
        <span className='w-50pxr h-20pxr skeleton-item rounded-2pxr'></span>
        <span className='w-16pxr h-16pxr skeleton-item rounded-2pxr'></span>
      </div>
      <div className='w-full grid grid-cols-2 grid-rows-2 gap-12pxr mb-20pxr'>
        {[...Array(20)].map((_, index) => (
          <TravelCardSkeleton key={index} />
        ))}
      </div>
    </>
  );
};

export default function TravelCardListPage() {
  const MORE_TRAVEL_CARDS_COUNT = 10;

  const observerTargetRef = useRef<HTMLDivElement>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);

  const [travelCards, setTravelCards] = useState<TravelCardCGType[]>([]);
  const [travelCardCategory, setTravelCardCategory] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(MORE_TRAVEL_CARDS_COUNT);

  useEffect(() => {
    const fetchTravelCards = async () => {
      const travelCards = await getListTravelCards();
      const travelCardCategory = await getTravelCardCategory();
      setTravelCards(travelCards);
      setTravelCardCategory(travelCardCategory);
      setIsLoading(false);
    };

    fetchTravelCards();
  }, []);

  useEffect(() => {
    setVisibleCount(MORE_TRAVEL_CARDS_COUNT);
  }, [searchQuery, selectedFilter]);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && !isLoading && visibleCount < travelCards.length) {
        setVisibleCount((prev) => Math.min(prev + MORE_TRAVEL_CARDS_COUNT, travelCards.length));
      }
    },
    [isLoading, visibleCount, travelCards.length, searchQuery],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null, // 뷰포트 기준
      rootMargin: '50px', // 하단에서 50px 떨어진 지점에서 트리거
      threshold: 0.1, // 10% 이상 보이면 트리거
    });

    if (observerTargetRef.current) {
      observer.observe(observerTargetRef.current);
    }

    return () => {
      if (observerTargetRef.current) {
        observer.unobserve(observerTargetRef.current);
      }
    };
  }, [handleObserver, searchQuery]);

  const filteredTravelCards = useMemo(() => {
    let cards = [...travelCards];

    if (selectedFilter) {
      cards = cards.filter((travelCard) => travelCard.continent === selectedFilter);
    }

    if (searchQuery !== '') {
      const searcher = new Hangul.Searcher(searchQuery.toUpperCase());
      cards = cards.filter((travelCard) => {
        return (
          searcher.search(travelCard.name) >= 0 ||
          searcher.search(travelCard.nameEng.toUpperCase()) >= 0 ||
          searcher.search(travelCard.country) >= 0
        );
      });
    }

    return cards;
  }, [travelCards, searchQuery, selectedFilter]);

  return (
    <div className='w-full pt-56pxr loaded-wrap'>
      <ThreeButtonsTopBar
        leftButton={<TopBarLeftButton />}
        centerTitle={<TopBarCenterTitle />}
        rightButton={<TopBarRightButton />}
      />
      <div className='w-full px-20pxr py-16pxr flex flex-col gap-20pxr'>
        <SearchInputBarBasic searchQuery={searchQuery} setSearchQuery={setSearchQuery} />

        {isLoading && <TravelCardPageSkeleton />}

        {!isLoading && (
          <SelectBoxSmall
            items={travelCardCategory}
            placeholder='대륙'
            selectedItem={selectedFilter}
            setSelectedItem={setSelectedFilter}
          />
        )}

        {!isLoading && (
          <>
            <div className='w-full grid grid-cols-2 grid-rows-2 gap-12pxr'>
              {filteredTravelCards.slice(0, visibleCount).map((travelCard) => (
                <TravelCard
                  key={travelCard.id}
                  id={travelCard.id}
                  imageUrl={travelCard.mainImageUrl}
                  name={`${travelCard.country} ${travelCard.name}`}
                  engName={travelCard.nameEng.toUpperCase()}
                />
              ))}
            </div>
            <div ref={observerTargetRef} className='col-span-2 h-10pxr'></div>
          </>
        )}
      </div>
    </div>
  );
}
